import { useEffect, useState, type ReactNode } from 'react'
import { Avatar, Box, Group, Stack, Text } from '@mantine/core'
import type { Chart as ChartJS } from 'chart.js'
import { resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import type { AiOperationsAnalyticsResponse, OperatorOverviewRow } from '@/types/api'
import {
  aiReasonHints,
  analyticsTooltipStyles,
  formatKtu,
  formatMinutes,
  formatNumber,
  formatPercent,
  initials,
  productTooltipStyles,
} from '@/pages/analytics/analytics-new-utils'
import { AnalyticsTooltipContent, UnifiedTooltip } from '@/pages/analytics/analytics-tooltips'

export function AxisIconRow({ items, chartRef }: { items: Array<{ key: string; name: string; color: string; imageUrl?: string | undefined; emoji?: string | undefined; tooltip: ReactNode; tooltipVariant?: 'default' | 'product'; onClick?: () => void }>; chartRef?: { current: ChartJS<'bar'> | null } }) {
  const [positions, setPositions] = useState<Array<{ left: number }>>([])

  useEffect(() => {
    if (items.length === 0 || !chartRef?.current) {
      setPositions([])
      return
    }
    let frame = 0
    const timers: number[] = []
    const update = () => {
      const chart = chartRef.current
      const scale = chart?.scales?.x
      const area = chart?.chartArea
      const width = area ? Math.max(0, area.right - area.left) : 0
      if (!chart || !scale || !area || width <= 0) {
        setPositions([])
        return
      }
      setPositions(
        items.map((_, index) => ({
          left: scale.getPixelForTick(index) - area.left,
        })),
      )
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }
    schedule()
    timers.push(window.setTimeout(schedule, 40))
    timers.push(window.setTimeout(schedule, 120))
    timers.push(window.setTimeout(schedule, 280))
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      timers.forEach((timer) => window.clearTimeout(timer))
      window.removeEventListener('resize', schedule)
    }
  }, [chartRef, items])

  if (items.length === 0) return null

  if (positions.length === items.length && chartRef?.current?.chartArea) {
    const area = chartRef.current.chartArea
    const width = Math.max(0, area.right - area.left)
    return (
      <Box
        style={{
          position: 'relative',
          width,
          marginLeft: area.left,
          minHeight: 40,
        }}
      >
        {items.map((item, index) => (
          <UnifiedTooltip key={item.key} label={item.tooltip} styles={item.tooltipVariant === 'product' ? productTooltipStyles : analyticsTooltipStyles} wrapperStyle={{ display: 'block', position: 'absolute', left: positions[index]?.left ?? 0, top: 0, transform: 'translateX(-50%)' }}>
            <Stack
              component="button"
              gap={2}
              align="center"
              onClick={item.onClick}
              style={{
                minWidth: 0,
                border: 0,
                padding: 0,
                margin: 0,
                background: 'transparent',
                cursor: item.onClick ? 'pointer' : 'default',
                pointerEvents: 'auto',
              }}
            >
              <Avatar
                src={item.imageUrl}
                size={30}
                radius="xl"
                style={{
                  background: '#fff',
                  color: 'var(--text-primary)',
                  boxShadow: `0 0 0 2px ${item.color}, 0 0 0 5px #fff`,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {item.emoji || initials(item.name)}
              </Avatar>
            </Stack>
          </UnifiedTooltip>
        ))}
      </Box>
    )
  }
  return <Box style={{ minHeight: 40 }} />
}

export function LegendOperatorRow({ op, value, previous, pct, color, selected, onToggle }: { op: OperatorOverviewRow; value: number; previous: number; pct: string; color: string; selected: boolean; onToggle: () => void }) {
  const name = op.operator_name || op.operator_email || '—'
  return (
    <Group component="button" onClick={onToggle} gap={6} wrap="nowrap" justify="space-between" style={{ width: '100%', border: '1px solid transparent', background: selected ? 'var(--bg-tertiary)' : 'color-mix(in srgb, var(--bg-tertiary) 55%, transparent)', borderRadius: 10, padding: '6px 8px', minHeight: 32, opacity: selected ? 1 : 0.42, cursor: 'pointer', textAlign: 'left' }}>
      <Group gap={8} wrap="nowrap" style={{ minWidth: 0, flex: '1 1 auto' }}>
        <UnifiedTooltip styles={analyticsTooltipStyles} wrapperStyle={{ display: 'inline-flex', flexShrink: 0 }} label={<AnalyticsTooltipContent title={name} rows={[{ label: 'Закрыто', value: formatNumber(value), tone: 'primary' }, { label: 'Предыдущий период', value: formatNumber(previous), tone: 'muted' }, { label: 'Ср. ответ', value: formatMinutes(op.avg_reply_minutes) }, { label: 'Ср. закрытие', value: formatMinutes(op.avg_close_minutes) }]} />}>
          <Avatar src={resolveOperatorAvatarSrc(op)} size={16} radius="xl" color="blue" style={{ background: color, flexShrink: 0, fontSize: 7, fontWeight: 700 }}>{initials(name)}</Avatar>
        </UnifiedTooltip>
        <Text truncate style={{ minWidth: 0, flex: '1 1 auto', fontSize: 11, lineHeight: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{name}</Text>
      </Group>
      <Text fw={700} style={{ whiteSpace: 'nowrap', marginLeft: 'auto', flexShrink: 0, fontSize: 10, lineHeight: '12px', color: '#4388ff' }}>{value} ({pct}%) <Text component="span" style={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 9, lineHeight: '11px' }}>· {formatNumber(previous)}</Text></Text>
    </Group>
  )
}

export function LegendProductRow({ name, color, imageUrl, emoji, value, pct, selected, onToggle }: { name: string; color: string; imageUrl?: string; emoji?: string; value: number; pct: string; selected: boolean; onToggle: () => void }) {
  return (
    <Group component="button" onClick={onToggle} gap={8} wrap="nowrap" justify="space-between" style={{ width: '100%', border: '1px solid transparent', background: selected ? 'var(--bg-tertiary)' : 'color-mix(in srgb, var(--bg-tertiary) 55%, transparent)', borderRadius: 10, padding: '7px 10px', minHeight: 36, opacity: selected ? 1 : 0.42, cursor: 'pointer', textAlign: 'left' }}>
      <Group gap={10} wrap="nowrap" style={{ minWidth: 0, flex: '1 1 auto' }}>
        <Avatar src={imageUrl} size={18} radius="xl" style={{ background: color, flexShrink: 0, fontSize: 8, fontWeight: 700 }}>{emoji || initials(name)}</Avatar>
        <Text truncate style={{ minWidth: 0, flex: '1 1 auto', fontSize: 11, lineHeight: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{name}</Text>
      </Group>
      <Text fw={700} style={{ whiteSpace: 'nowrap', marginLeft: 'auto', flexShrink: 0, fontSize: 10, lineHeight: '12px', color: '#4388ff' }}>{value} ({pct}%)</Text>
    </Group>
  )
}

export function LegendSourceRow({ name, color, imageUrl, emoji, avatar, value, previous, pct }: { name: string; color: string; imageUrl?: string; emoji?: string; avatar?: boolean; value: number; previous: number; pct: string }) {
  return (
    <Group gap={8} wrap="nowrap" justify="space-between" style={{ width: '100%', border: '1px solid transparent', background: 'color-mix(in srgb, var(--bg-tertiary) 55%, transparent)', borderRadius: 10, padding: '7px 10px', minHeight: 36, textAlign: 'left' }}>
      <Group gap={10} wrap="nowrap" style={{ minWidth: 0, flex: '1 1 auto' }}>
        {avatar || imageUrl || emoji ? (
          <Avatar src={imageUrl} size={18} radius="xl" style={{ background: color, flexShrink: 0, fontSize: 8, fontWeight: 700 }}>{emoji || initials(name)}</Avatar>
        ) : (
          <Box w={18} h={18} style={{ background: color, borderRadius: 6, flexShrink: 0 }} />
        )}
        <Text truncate style={{ minWidth: 0, flex: '1 1 auto', fontSize: 11, lineHeight: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{name}</Text>
      </Group>
      <Text fw={700} style={{ whiteSpace: 'nowrap', marginLeft: 'auto', flexShrink: 0, fontSize: 10, lineHeight: '12px', color: '#4388ff' }}>
        {formatNumber(value)} ({pct}%)
        <Text component="span" style={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 9, lineHeight: '11px' }}> · {formatNumber(previous)}</Text>
      </Text>
    </Group>
  )
}

export function LegendKtuRow({ name, color, imageUrl, emoji, value, previous, selected, onToggle, formatter = formatKtu }: { name: string; color: string; imageUrl?: string; emoji?: string; value: number; previous: number; selected: boolean; onToggle: () => void; formatter?: (value: number) => string }) {
  return (
    <Group component="button" onClick={onToggle} gap={8} wrap="nowrap" justify="space-between" style={{ width: '100%', border: '1px solid transparent', background: selected ? 'var(--bg-tertiary)' : 'color-mix(in srgb, var(--bg-tertiary) 55%, transparent)', borderRadius: 10, padding: '7px 10px', minHeight: 36, opacity: selected ? 1 : 0.42, cursor: 'pointer', textAlign: 'left' }}>
      <Group gap={10} wrap="nowrap" style={{ minWidth: 0, flex: '1 1 auto' }}>
        <Avatar src={imageUrl} size={18} radius="xl" style={{ background: color, flexShrink: 0, fontSize: 8, fontWeight: 700 }}>{emoji || initials(name)}</Avatar>
        <Text truncate style={{ minWidth: 0, flex: '1 1 auto', fontSize: 11, lineHeight: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{name}</Text>
      </Group>
      <Text fw={700} style={{ whiteSpace: 'nowrap', marginLeft: 'auto', flexShrink: 0, fontSize: 10, lineHeight: '12px', color: '#4388ff' }}>{formatter(value)} <Text component="span" style={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 9, lineHeight: '11px' }}>· {formatter(previous)}</Text></Text>
    </Group>
  )
}

export function LegendAiReasonRow({ reasonKey, name, value, previous, pctTransfers, pctTotal, onClick }: { reasonKey: keyof typeof aiReasonHints; name: string; value: number; previous: number; pctTransfers: number; pctTotal: number; onClick?: () => void }) {
  const tooltip = (
    <AnalyticsTooltipContent
      title={name}
      rows={[
        { label: 'Что считается', value: aiReasonHints[reasonKey] },
        { label: 'Текущий период', value: formatNumber(value), tone: 'primary' },
        { label: 'Предыдущий период', value: formatNumber(previous), tone: 'muted' },
        { label: '% от передач', value: formatPercent(pctTransfers) },
        { label: '% от всех AI-обращений', value: formatPercent(pctTotal) },
      ]}
    />
  )
  return (
    <UnifiedTooltip label={tooltip} styles={analyticsTooltipStyles} wrapperStyle={{ display: 'block', width: '100%' }}>
    <Stack component="button" onClick={onClick} gap={5} style={{ width: '100%', border: '1px solid transparent', background: 'color-mix(in srgb, var(--bg-tertiary) 55%, transparent)', borderRadius: 10, padding: '8px 10px', minHeight: 52, justifyContent: 'space-between', textAlign: 'left', cursor: onClick ? 'pointer' : 'default' }}>
      <Group gap={6} wrap="nowrap" align="flex-start">
        <Text style={{ minWidth: 0, flex: '1 1 auto', fontSize: 11, lineHeight: '14px', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{name}</Text>
      </Group>
      <Text fw={700} style={{ fontSize: 10, lineHeight: '12px', color: '#4388ff', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}>
        {formatNumber(value)}
        <Text component="span" style={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 9, lineHeight: '11px' }}> · {formatNumber(previous)}</Text>
        <Text component="span" style={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 9, lineHeight: '11px' }}> · {formatPercent(pctTransfers)} / {formatPercent(pctTotal)}</Text>
      </Text>
    </Stack>
    </UnifiedTooltip>
  )
}

export function LegendAiProjectMetric({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'primary' | 'success' | 'default' }) {
  const valueColor = tone === 'primary' ? '#4388ff' : tone === 'success' ? '#0f9f55' : 'var(--text-primary)'
  return (
    <Stack gap={1} style={{ minWidth: 0 }}>
      <Text
        style={{
          fontSize: 9,
          lineHeight: '11px',
          fontWeight: 600,
          color: 'var(--text-tertiary)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontSize: 10,
          lineHeight: '12px',
          fontWeight: 800,
          color: valueColor,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Text>
    </Stack>
  )
}

export function LegendAiProjectRow({ name, color, imageUrl, emoji, row, previous }: { name: string; color: string; imageUrl?: string; emoji?: string; row: NonNullable<AiOperationsAnalyticsResponse['by_project']>[number]; previous?: NonNullable<AiOperationsAnalyticsResponse['by_project']>[number] }) {
  const tooltip = (
    <AnalyticsTooltipContent
      title={name}
      rows={[
        { label: 'Всего обращений', value: formatNumber(row.total_tickets), tone: 'primary' },
        { label: 'База без аварий', value: formatNumber(row.non_emergency_tickets) },
        { label: 'Аварийных', value: formatNumber(row.emergency_tickets) },
        { label: 'Передано оператору', value: `${formatNumber(row.ai_transferred)} (${formatPercent(row.ai_transferred_pct_of_total)})` },
        { label: 'Решено / закрыто AI', value: `${formatNumber(row.ai_resolved_closed)} (${formatPercent(row.ai_resolved_closed_pct_of_total)})` },
        { label: 'В работе у AI', value: `${formatNumber(row.ai_in_progress)} (${formatPercent(row.ai_in_progress_pct_of_total)})` },
        { label: 'Предыдущий период', value: formatNumber(previous?.non_emergency_tickets), tone: 'muted' },
        { label: 'Как считать', value: 'База проекта без аварий раскладывается на три взаимоисключающие части: передано оператору, решено AI, в работе у AI. Их сумма = 100%.' },
      ]}
    />
  )
  return (
    <UnifiedTooltip label={tooltip} styles={analyticsTooltipStyles} wrapperStyle={{ display: 'block', width: '100%' }}>
    <Stack gap={7} style={{ width: '100%', border: '1px solid transparent', background: 'color-mix(in srgb, var(--bg-tertiary) 55%, transparent)', borderRadius: 10, padding: '8px 10px', minHeight: 86, textAlign: 'left', overflow: 'hidden' }}>
      <Group gap={8} wrap="nowrap" align="flex-start">
        <Avatar src={imageUrl} size={20} radius="xl" style={{ background: color, flexShrink: 0, fontSize: 8, fontWeight: 700 }}>{emoji || initials(name)}</Avatar>
        <Stack gap={1} style={{ minWidth: 0, flex: '1 1 auto' }}>
          <Group gap={6} wrap="nowrap" align="center">
            <Text truncate style={{ minWidth: 0, flex: '1 1 auto', fontSize: 12, lineHeight: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{name}</Text>
          </Group>
          <Text style={{ fontSize: 9, lineHeight: '11px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            база: {formatNumber(row.non_emergency_tickets)} · пред.: {formatNumber(previous?.non_emergency_tickets)}
          </Text>
        </Stack>
        <Text fw={800} style={{ whiteSpace: 'nowrap', flex: '0 0 auto', fontSize: 11, lineHeight: '14px', color: '#4388ff', fontVariantNumeric: 'tabular-nums' }}>
          {formatNumber(row.non_emergency_tickets)}
        </Text>
      </Group>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: '6px 10px',
          minWidth: 0,
        }}
      >
        <LegendAiProjectMetric label="Передано" value={`${formatNumber(row.ai_transferred)} (${formatPercent(row.ai_transferred_pct_of_total)})`} tone="primary" />
        <LegendAiProjectMetric label="Решено AI" value={`${formatNumber(row.ai_resolved_closed)} (${formatPercent(row.ai_resolved_closed_pct_of_total)})`} tone="success" />
        <LegendAiProjectMetric label="В работе" value={`${formatNumber(row.ai_in_progress)} (${formatPercent(row.ai_in_progress_pct_of_total)})`} />
        <LegendAiProjectMetric label="Аварии" value={formatNumber(row.emergency_tickets)} />
      </Box>
    </Stack>
    </UnifiedTooltip>
  )
}
